/**
 * Cross-tab auth event synchronization via BroadcastChannel.
 *
 * When a user logs out or changes their password in one tab, all other
 * tabs must be notified so they don't continue operating with stale state.
 */

type AuthEvent =
  | { type: "logout" }
  | { type: "password_changed" }
  | { type: "session_expired" };

type AuthEventCallback = (event: AuthEvent) => void;

const CHANNEL_NAME = "mi-auth";

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
  }
  return channel;
}

/**
 * Broadcast an auth event to all other tabs.
 * No-op if BroadcastChannel is unavailable (SSR, older browsers).
 */
export function postAuthEvent(event: AuthEvent): void {
  getChannel()?.postMessage(event);
}

/**
 * Subscribe to auth events from other tabs.
 * Returns an unsubscribe function.
 */
export function onAuthEvent(callback: AuthEventCallback): () => void {
  const ch = getChannel();
  if (!ch) return () => {};

  const handler = (e: MessageEvent<AuthEvent>) => {
    callback(e.data);
  };
  ch.addEventListener("message", handler);
  return () => ch.removeEventListener("message", handler);
}
